export interface ModifierGroupInfo {
  id: string;
  name: string;
  description: string;
  nameTranslations?: NameTranslationType;
  descriptionTranslations?: NameTranslationType;
  modifierProducts: ModifierProductInfo[];
}

export interface CreateModifierGroupRequest extends UpdateModifierGroupRequest {
  organizationId: string;
}

export interface UpdateModifierGroupRequest {
  name: string;
  description: string;
  nameTranslations?: NameTranslationType;
  descriptionTranslations?: NameTranslationType;
  modifierProducts: ModifierProductInfo[];
  organizationId: string;
}

export interface ModifierProductInfo {
  modifierId: string;
}

export interface Promo {
  dishes: PromoDish[];
}

export interface PromoDish {
  id: string;
  order: number;
}

export interface AllergenGroup {
  externalId?: string;
  name: string;
  code: string;
  nameTranslations?: NameTranslationType;
  isManuallyCreated?: boolean;
  externalPictureUrl?: string;
  textColor?: string;
  backgroundColor?: string;
}

export interface AllergenGroupRequestDto {
  name: string;
  nameTranslations?: NameTranslationType;
  textColor?: string;
  backgroundColor?: string;
}

export interface DisplayedAllergenGroup extends AllergenGroup {
  id: string;
}

export interface AllergenGroupOverride{
  name?: string;
  nameTranslations?: NameTranslationType;
  pictureUrl?: string;
  textColor?: string;
  backgroundColor?: string;
}

export interface TagDto{
  id: string;
  externalId?: string;
  name: string;
  nameTranslations?: NameTranslationType;
  externalPictureUrl?: string;
  isManuallyCreated: boolean;
  textColor?: string;
  backgroundColor?: string;
}

export interface TagRequestDto {
  name: string;
  nameTranslations?: NameTranslationType;
  externalPictureUrl?: string;
  textColor?: string;
  backgroundColor?: string;
}

export interface TagOverride {
  name?: string;
  nameTranslations?: NameTranslationType;
  externalPictureUrl?: string;
  textColor?: string;
  backgroundColor?: string;
}

export interface GecoPosMenu {
  groups: Group[];
  products: Product[];
  comboGroups: ComboGroup[];
  sizes: Size[];
  modifierGroups: ModifierGroup[];
  sizeScales: DisplayedSizeScale[];
  allergenGroups: DisplayedAllergenGroup[];
  tags?: TagDto[];
  modifierGroupTypes: ModifierGroupType[];
  relatedProductsPrograms: RelatedProductsProgram[]
  promo?: Promo;
  isSynced: boolean;
}

export interface Dictionary<T> {
  [key: string]: T;
}

export interface MenuBackup{
  id: string;
  name: string;
  basedMenuSectionId: string;
  createdAt: string;
  organizationId: string;
}

export interface CreateMenuBackup {
  name: string;
  basedMenuSectionId: string;
}

export interface GetMenuRequest {
  organizationId: string;
  menuName: string;
}

export interface MenuSection {
  id: string;
  name: string;
  description?: string;
  menuId: string;
  isDeleted: boolean;
  parentMenuSectionId?: string;
}

export interface BatchOverridePositionsRequest {
  overridePositions: OverridePositionRequest[];
  menuSectionIds: string[];
}

export interface OverridePosition {
  id?: string;
  positionType: PositionTypeEnum;
  positionId: string;
  override: string;
  menuSectionId: string;
}

export class OverridePositionsMap {

  models: OverridePosition[] = [];
  _map: Map<PositionTypeEnum, Map<string, OverridePosition>> = new Map();

  constructor(params: OverridePosition[] | Map<PositionTypeEnum, Map<string, OverridePosition>>) {
    if (params instanceof Map<PositionTypeEnum, Map<string, OverridePosition>>){
      this._map = params;
    }
    else{
      Object.values(PositionTypeEnum).forEach(value => {
        this._map.set(value as unknown as PositionTypeEnum, new Map())
      })

      params.forEach(override => {
        this._map.get(override.positionType).set(override.positionId, override);
      })

      this.models = params;
    }
  }

  getOverride(positionType: PositionTypeEnum, positionId: string): OverridePosition | undefined {
    return this._map.get(positionType)?.get(positionId);
  }

  addOrUpdateOverride(overridePosition: OverridePosition): void {
    this._map.get(overridePosition.positionType).set(overridePosition.positionId, overridePosition);
  }

  copy(): OverridePositionsMap{
    return new OverridePositionsMap(this._map);
  }
}

export const emptyOverride = '{}';

export interface OverrideInstruction {
  key: string,
  value: string,
  type: JsonObjectTypeEnum,
  instruction: OverrideInstructionEnum,
}

export interface OverridePositionRequest extends OverridePosition {
  instructions: OverrideInstruction[];
}

export interface CreateGroupRequest extends Group {
  menuSectionId: string;
}

export interface UploadImageRequest {
  organizationId: string;
  imageUrl?: string;
  imageBase64?: string;
  IsDeletedPicture: boolean;
}

export interface UploadVideoRequest {
  organizationId: string;
  videoUrl?: string;
  videoBase64?: string;
  IsDeletedPicture: boolean;
  format?: string;
}

export interface UpdateMenuSectionRequest extends CreateMenuSectionRequest{
  id: string;
}

export interface CreateMenuSectionRequest {
  name: string;
  description?: string;
  copyFromMenuSectionId?: string;
  parentMenuSectionId?: string;
  isDeleted: boolean;
  organizationId: string;
}

export interface MenuEvent {
  id: string;
  entityId: string;
  entity: MenuEventEntityEnum;
  menuSectionId: string;
  userId: string;
  userEmail: string;
  oldData: string;
  newData: string;
  createdAt: Date;
}

export enum MenuEventEntityEnum{
  Menu = 'Menu',
  Group = 'Group',
  GroupStructure = 'GroupStructure',
  Product = 'Product',
  ProductStructure = 'ProductStructure',
  ProductInGroup = 'ProductInGroup',
  Size = 'Size',
  SizeStructure = 'SizeStructure',
  ModifierGroup = 'ModifierGroup',
  ModifierGroupStructure = 'ModifierGroupStructure',
  ModifierProductStructure = 'ModifierProductStructure',
  ComboStructure = 'ComboStructure',
  ComboGroup = 'ComboGroup',
  ComboGroupStructure = 'ComboGroupStructure',
  ComboProductStructure = 'ComboProductStructure',
}

export interface RelatedProductsInfo {
  name?: string;
  nameTranslations?: NameTranslationType;
  products?: string[];
  legacy?: boolean;
  // Admin-only chip label, отдельно от name. Хранится в override JSON, в DTO для клиентов не идёт.
  label?: string;
}

export interface RelatedProductsProgram {
  id: string;
  name?: string;
  nameTranslations?: NameTranslationType;
  products?: string[];
  isManuallyCreated: boolean;
}

export interface RelatedProductsProgramOverride {
  name?: string;
  nameTranslations?: NameTranslationType;
  products?: string[];
}


export interface UpdateRelatedProductsProgram {
  menuId: string;
  name?: string;
  nameTranslations?: NameTranslationType;
  products?: string[];
}

export interface HasRelatedProductsPrograms {
  relatedProductsProgramIds?: string[];
}

export interface Group extends HasRelatedProductsInfo{
  externalId: string;
  name: string;
  description: string;
  nameTranslations: NameTranslationType;
  descriptionTranslations: object;
  tags: string[];
  tagIds: string[];
  externalPictureUrl?: string;
  metaInfo?: string;
  groupStructure: GroupStructure;
  hasVipDish?: boolean;
}

export interface LegacyRelatedProducts {
  relatedProductsName?: string;
  relatedProductsNameTranslations?: NameTranslationType;
  relatedProducts?: string[];
}

export interface HasRelatedProductsInfo {
  relatedProductsInfo?: RelatedProductsInfo[];
}

export interface DisplayedGroup extends Group {
  order: number;
  hasReOrderingProductInGroup: boolean;
  assortmentHasChanged: boolean;
  isFiltered: boolean;
  isChangedName: boolean;
}

export interface GroupStructure {
  productsInGroup: ProductInGroup[];
  parentId?: string;
  order?: number;
}

export interface ProductInGroup {
  externalProductId: string;
  order: number;
}

// КБЖУ на 100 г. Каждое поле nullable: null — значение не задано (для оверрайда — не переопределено).
export interface Nutrition {
  fats?: number | null;
  proteins?: number | null;
  carbs?: number | null;
  energy?: number | null;
}

export const nutritionFields = ['fats', 'proteins', 'carbs', 'energy'] as const;
export type NutritionField = typeof nutritionFields[number];

export interface Product extends LegacyRelatedProducts, HasRelatedProductsInfo, HasRelatedProductsPrograms {
  externalId: string;
  name: string;
  description: string;
  detailedDescription?: string;
  sku: string;
  nameTranslations: NameTranslationType;
  descriptionTranslations: object;
  detailedDescriptionTranslations?: NameTranslationType;
  tags: string[];
  tagIds: string[];
  weight: number;
  nutrition?: Nutrition | null;
  measure: string;
  metaInfo?: string;
  externalPictureUrl?: string;
  mediaInfo?: MediaInfo;
  hideSizesImages?: boolean;
  productStructure?: ProductStructure;
  comboStructure?: ComboStructure;
}

export interface MediaInfo{
  menuMediaUrl?: string;
  cardMediaUrl?: string;
}
export interface ProductStructure {
  price: number;
  type: string;
  sizeScaleId?: string;
  defaultSizeId?: string;
  sizes: SizeStructure[];
  modifierGroups: ModifierGroupStructure[];
  allergenGroups?: string[];
  modifiersDisplayType?: string;
}

export interface SizeScale {
  name: string;
  nameTranslations: NameTranslationType;
  visualType: string;
}

export interface DisplayedSizeScale extends SizeScale{
  id: string;
}

export interface SizeScaleRequestDto extends SizeScale{
  organizationId: string;
}

export interface Size {
  externalId: string;
  name: string;
  nameTranslations: NameTranslationType;
}

export interface DisplayedSize extends Size {
  isChangedSize: boolean;
}

export interface SizeStructure {
  sizeId: string;
  isHidden: boolean;
  order: number;
  price: number;
  externalPictureUrl?: string;
  weight: number;
  nutrition?: Nutrition | null;
}

export interface ModifierGroup {
  externalId: string;
  name: string;
  description: string;
  nameTranslations: NameTranslationType;
  descriptionTranslations: object;
  metaInfo: string;
  modifierGroupTypeId?: string;
}

export interface ModifierGroupType extends ModifierGroupTypeUpdateDto{
  id: string;
}

export interface ModifierGroupTypeUpdateDto {
  typeName: string;
  addingType: string;
  visualType: string;
}

export interface ModifierGroupStructure {
  modifierGroupId: string;
  required: boolean;
  isHidden: boolean;
  order: number;
  min: number;
  max: number;
  modifierProducts: ModifierProductStructure[];
  childModifiersHaveMinMaxRestrictions: boolean;
  hideImages?: boolean;
}

export interface ModifierProductStructure {
  modifierId: string;
  isHidden: boolean;
  order: number;
  min: number;
  max: number;
  defaultAmount: number;
}

export interface ComboStructure {
  price: number;
  type: string;
  offerToCollect: boolean;
  groups: ComboGroupStructure[];
}

export interface ComboGroup {
  externalId: string;
  name: string;
  nameTranslations: NameTranslationType;
}

export interface ComboGroupStructure {
  comboGroupId: string;
  isHidden: boolean;
  order: number;
  products: ComboProductStructure[]
}

export interface ComboProductStructure {
  productId: string;
  externalProductId: string;
  isHidden?: boolean;
  order?: number;
  sizeId?: string;
  price: number;
}

export interface DisplayedProduct extends Product {
  isAnyChanged: boolean;
  isChangedPrice: boolean;
  isChangedName: boolean;
  productStructure?: DisplayedProductStructure;
  comboStructure?: DisplayedComboStructure;
}

export interface DisplayedProductStructure extends ProductStructure {
  isChangedPrice: boolean;
  isChangedSizeScaleId: boolean;
  sizes: DisplayedSizeStructure[];
}

export interface DisplayedComboStructure extends ComboStructure {
  isChangedPrice: boolean;
  sizes: DisplayedSizeStructure[];
  groups: DisplayedComboGroupStructure[];
}

export interface DisplayedSizeStructure extends SizeStructure {
  isChangedPrice: boolean;
}

export interface DisplayedComboProductStructure extends ComboProductStructure {
  isChangedPrice: boolean;
  isChangedName: boolean;
  isChangedVisibility: boolean;
}

export interface DisplayedComboGroupStructure extends ComboGroupStructure {
  products: DisplayedComboProductStructure[]
}

export interface GetMenuSyncOfExternalPOSResponse {
  externalPOS: string;
  lastSyncedAt?: Date;
  pipeId: string;
}

export interface GetMenuSyncsOfSaleSystemsResponse {
  records: { [menuId: string]: MenuSyncOfSaleSystem[] };
}

export interface MenuSyncOfSaleSystem {
  saleSystem: string;
  lastSyncedAt?: Date;
  pipeId: string;
}

export interface MenuSyncOfSale {
  service: string;
  lastSyncedAt?: Date;
  pipeId: string;
  menuId: string;
}

export interface MenuSyncOfPOS {
  pos: string;
  lastSyncedAt?: Date;
  pipeId: string;
}

export interface MenuOverride {
  groupIds?: string[];
}

export interface GroupOverride extends LegacyRelatedProducts, HasRelatedProductsInfo{
  name?: string;
  tags?: string[];
  tagIds?: string[];
  description?: string;
  nameTranslations?: NameTranslationType;
  descriptionTranslations?: NameTranslationType;
  externalPictureUrl?: string;
  hasVipDish?: boolean;
}

export interface GroupStructureOverride {
  productIds?: string[];
  parentId?: string;
}

export interface ProductOverride extends LegacyRelatedProducts, HasRelatedProductsInfo, HasRelatedProductsPrograms {
  name?: string;
  externalPictureUrl?: string;
  tags?: string[];
  tagIds?: string[];
  weight?: number;
  nutrition?: Nutrition | null;
  description?: string;
  detailedDescription?: string;
  nameTranslations?: NameTranslationType;
  descriptionTranslations?: NameTranslationType;
  detailedDescriptionTranslations?: NameTranslationType;
  hideSizesImages?: boolean;
  menuMediaUrl?: string;
  cardMediaUrl?: string;
}

export interface ProductStructureOverride {
  price?: number;
  sizeScaleId?: string;
  defaultSizeId?: string;
  allergenGroups?: string[];
  modifiersDisplayType?: string;
  offerToCollect?: boolean;
}

export interface ModifierGroupOverride {
  name?: string;
  nameTranslations?: NameTranslationType;
  modifierGroupTypeId?: string;
}

export interface ModifierProductStructureOverride {
  isHidden?: boolean;
  order?: number;
  min?: number;
  max?: number;
  defaultAmount?: number;
}

export interface ModifierGroupStructureOverride {
  required?: boolean;
  isHidden?: boolean;
  order?: number;
  min?: number;
  max?: number;
  hideImages?: boolean;
}

export interface SizeOverride {
  name?: string;
  nameTranslations?: NameTranslationType;
}

export interface ComboGroupOverride {
  name?: string;
  nameTranslations?: NameTranslationType;
}

export interface ComboProductStructureOverride {
  isHidden?: boolean;
  order?: number;
  sizeId?: string;
  price?: number;
}

export interface SizeStructureOverride {
  price?: number;
  order?: number;
  isHidden?: boolean;
  externalPictureUrl?: string;
  weight?: number;
  nutrition?: Nutrition | null;
}

export enum PositionTypeEnum {
  Menu = 1,
  Group = 2,
  GroupStructure = 3,
  Product = 4,
  ProductStructure = 5,
  ProductInGroup = 6,
  Size = 7,
  SizeStructure = 8,
  ModifierGroup = 9,
  ModifierGroupStructure = 10,
  ModifierProductStructure = 11,
  ComboStructure = 12,
  ComboGroup = 13,
  ComboGroupStructure = 14,
  ComboProductStructure = 15,
  AllergenGroups = 16,
  Tags = 17,
  RelatedProductsPrograms = 18,
}

export enum OverrideInstructionEnum {
  Add = 0,
  Change = 1,
  Delete = 2,
}

export enum JsonObjectTypeEnum {
  StringType = 0,
  NumberType = 1,
  BooleanType = 2,
  ArrayType = 3,
  ObjectType = 4,
}

export const ModifierGroupAddingTypeEnum : string[] = [
  'Addon',
  'Removals',
]

export const ModifierGroupVisualTypeEnum : string[] = [
  'Default',
  'Window',
  'Widget',
  'Popup',
  'Minimal',
]

export type NameTranslationType = { [key: string]: string};

export interface MenuEventsFilterRequest {
  entityId?: string;
  entity?: MenuEventEntityEnum;
  userId?: string;
  menuId?: string;
  menuSectionId?: string;
  fromCreatedAt?: string;
  toCreatedAt?: string;
  pageSize: number;
  pageIndex: number;
}

export interface MenuEventsQueryParams extends MenuEventsFilterRequest {
}

export interface MenuEventsFormFilter extends MenuEventsFilterRequest {
}

